import React, { useEffect, useState } from 'react';
import Fuse from 'fuse.js';
import { PageContentLayout } from '../../../shared/layout';
import { PlusOutlined, SearchOutlined, TagOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { PrimaryTableRowText, TableColumnTitle } from '../../../shared/globalStyling/styledText';
import {
  Button,
  Card,
  Grid,
  Input,
  Select,
  Table,
  Tabs,
  Space,
  message,
  Typography,
} from 'antd';
import DocumentationAlert from './components/DocumentationAlert';
import MainTable, { MetricCard } from './components/MainTable';
import TagMobileCard from './components/TagMobileCard';
import TagsPagination from './components/TagsPagination';
import RequestAssistance from './components/RequestAssitance';
import { withRouter } from 'react-router-dom';
import CreateImpressionsTag from '../../SignalTags/main/components/CreateImpressionsTag';
import CreatePublisherDomainTag from '../../SignalTags/main/components/CreatePublsiherDomainTag';
import BackToTop from '../../Tasks/components/shared/BackToTop';
import TagHelpCenterModal from './components/TagHelpCenterModal';
import '../styles/index.scss';
const { useBreakpoint } = Grid;
const { Search } = Input;
const { TabPane } = Tabs;

const performFuzzySearch = (data, searchKeys, searchValue) => {
  const keys = Array.isArray(searchKeys) ? searchKeys : [searchKeys];
  const options = {
    keys,
    threshold: 0.3,
  };

  const fuse = new Fuse(data, options);
  return fuse.search(searchValue).map((result) => result.item);
};

const TagsMainView = ({
  history,
  location,
  requestAssistance,
  publishersTags,
  impressionsTags,
  measurementsTags,
  onImressionsTagCreate,
  refetch,
  isAttentionSignals,
  advertisers,
  testTag,
  basicTagActivity,
  clearBasicTagActivity,
}) => {
  document.title = 'Tags Tracking Main View';
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const isCardView = !screens.xl; // Cards below 1200px (laptop + tablet + mobile)
  const params = new URLSearchParams(location.search);
  const status = params.get('status') ? params.get('status') : 'live';
  const tagId = params.get('tagId');
  const name = params.get('name');
  const publisherTagId = params.get('publisherTagId');
  const publisherName = params.get('publisherName');
  const impressionTagId = params.get('impressionTagId');
  const impressionName = params.get('impressionName');
  const initialTab = params.get('tab') || 'measurement';

  const [activeTab, setActiveTab] = useState(initialTab);
  const [filteredPublisherTags, setFilteredPublisherTags] = useState();
  const [filteredImpressionTags, setFilteredImpressionTags] = useState();
  const [filteredMeasurementTags, setFilteredMeasurementTags] = useState();
  const [searchValue, setSearchValue] = useState('');
  const [pubPage, setPubPage] = useState(1);
  const [pubPageSize, setPubPageSize] = useState(10);
  const [impPage, setImpPage] = useState(1);
  const [impPageSize, setImpPageSize] = useState(10);
  const [pubVisible, setPubVisible] = useState(10);
  const [impVisible, setImpVisible] = useState(10);
  const [helpCenterVisible, setHelpCenterVisible] = useState(false);
  const LOAD_MORE_BATCH = 10;

  useEffect(() => {
    if (!publishersTags) return;
    if (searchValue && activeTab === 'domain') {
      setFilteredPublisherTags(
        performFuzzySearch(publishersTags, searchKeysMap.domain, searchValue)
      );
    } else {
      setFilteredPublisherTags(publishersTags);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishersTags]);

  useEffect(() => {
    if (!impressionsTags) return;
    if (searchValue && activeTab === 'impressions') {
      setFilteredImpressionTags(
        performFuzzySearch(impressionsTags, searchKeysMap.impressions, searchValue)
      );
    } else {
      setFilteredImpressionTags(impressionsTags);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impressionsTags]);

  useEffect(() => {
    if (!measurementsTags) return;
    if (searchValue && activeTab === 'measurement') {
      setFilteredMeasurementTags(
        performFuzzySearch(measurementsTags, searchKeysMap.measurement, searchValue)
      );
    } else {
      setFilteredMeasurementTags(measurementsTags);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measurementsTags]);

  // Backward compat: apply initial URL search params for measurement
  useEffect(() => {
    const initial = params.get('search') || params.get('tagId') || params.get('name') || '';
    if (initial && measurementsTags) {
      setSearchValue(initial);
      setFilteredMeasurementTags(
        performFuzzySearch(measurementsTags, ['name', 'appId'], initial)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measurementsTags]);

  useEffect(() => {
    if (publishersTags && publisherTagId) {
      const searchTags = performFuzzySearch(publishersTags, 'tagId', publisherTagId);
      setFilteredPublisherTags(searchTags);
    }
  }, [publishersTags, history, publisherTagId]);

  useEffect(() => {
    if (publishersTags && publisherName) {
      const searchTags = performFuzzySearch(publishersTags, 'name', publisherName);
      setFilteredPublisherTags(searchTags);
    }
  }, [publishersTags, history, publisherName]);

  useEffect(() => {
    if (impressionsTags && impressionTagId) {
      const searchTags = performFuzzySearch(impressionsTags, 'tagId', impressionTagId);
      setFilteredImpressionTags(searchTags);
    }
  }, [impressionsTags, history, impressionTagId]);

  useEffect(() => {
    if (impressionsTags && impressionName) {
      const searchTags = performFuzzySearch(impressionsTags, 'name', impressionName);
      setFilteredImpressionTags(searchTags);
    }
  }, [impressionsTags, history, impressionName]);

  useEffect(() => {
    if (basicTagActivity) {
      message.success('Signal Tested Successfully', 3);
      clearBasicTagActivity();
    } else if (basicTagActivity === "") {
      message.error('Error Testing Signal: cannot fetch tag activity');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basicTagActivity]);

  const publisherColumn = [
    {
      title: <TableColumnTitle text={'Tag Name'} />,
      key: 'name',
      width: 280,
      render: (record) => (
        <div className="tags-main__name-cell">
          <PrimaryTableRowText text={record.name} />
          {record.advertiser && (
            <Typography.Text type="secondary" className="tags-main__advertiser">
              {record.advertiser}
            </Typography.Text>
          )}
        </div>
      ),
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: <TableColumnTitle text={'Domain'} />,
      key: 'domain',
      width: 160,
      ellipsis: true,
      render: (record) => record.domain,
      sorter: (a, b) => (a.domain || '').localeCompare(b.domain || ''),
    },
    {
      title: <TableColumnTitle text={'Measured Events'} />,
      key: 'pageViews',
      width: 260,
      render: (record) => (
        <MetricCard
          label="Page Views"
          value={record.pageViews}
          date={record.date !== 'Signal Unknown' ? record.date : null}
          status={record.color}
        />
      ),
    },
    {
      title: <TableColumnTitle text={'Tag ID'} />,
      dataIndex: 'tagId',
      key: 'tagId',
      width: 340,
      render: (text) => (
        <Typography.Text
          copyable={{
            text,
            tooltips: ['Copy Tag ID', 'Copied!'],
          }}
          className="tags-main__tag-id"
          onClick={(event) => event.stopPropagation()}
        >
          {text}
        </Typography.Text>
      ),
      sorter: (a, b) => (a.tagId || '').localeCompare(b.tagId || ''),
    },
    {
      title: <TableColumnTitle text={'Action'} />,
      key: 'action',
      width: 140,
      render: (record) => (
        <Button
          type="primary"
          icon={<TagOutlined />}
          onClick={async (event) => {
            event.stopPropagation();
            clearBasicTagActivity(); // Clear previous state before starting new test
            testTag({
              appId: record.tagId,
              orgId: record.orgId,
              tagId: record.id,
            });
            message.info(
              `Signal Tag Test for ${record.name} initiated. May take a couple of minutes to complete.`
            );
          }}
        >
          Test Signal
        </Button>
      ),
    },
  ];

  const impressionColumn = [
    {
      title: <TableColumnTitle text={'Tag Name'} />,
      key: 'name',
      width: 260,
      render: (record) => record.name,
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: <TableColumnTitle text={'Platform'} />,
      key: 'dsp',
      width: 160,
      render: (record) => record.dsp,
      sorter: (a, b) => (a.dsp || '').localeCompare(b.dsp || ''),
    },
    {
      title: <TableColumnTitle text={'Measured Events'} />,
      key: 'impressions',
      width: 260,
      render: (record) => (
        <MetricCard
          label="Impressions"
          value={record.impressions}
          date={record.date !== 'Signal Unknown' ? record.date : null}
          status={record.color}
        />
      ),
    },
    {
      title: <TableColumnTitle text={'Tag ID'} />,
      dataIndex: 'tagId',
      key: 'tagId',
      width: 340,
      render: (text) => (
        <Typography.Text
          copyable={{
            text,
            tooltips: ['Copy Tag ID', 'Copied!'],
          }}
          className="tags-main__tag-id"
          onClick={(event) => event.stopPropagation()}
        >
          {text}
        </Typography.Text>
      ),
      sorter: (a, b) => (a.tagId || '').localeCompare(b.tagId || ''),
    },
    {
      title: <TableColumnTitle text={'Action'} />,
      key: 'action',
      width: 140,
      render: (record) => (
        <Button
          type="primary"
          icon={<TagOutlined />}
          onClick={async (event) => {
            event.stopPropagation();
            clearBasicTagActivity(); // Clear previous state before starting new test
            testTag({
              appId: record.tagId,
              orgId: record.orgId,
              tagId: record.id,
            });
            message.info(
              `Signal Tag Test for ${record.name} initiated. May take a couple of minutes to complete.`
            );
          }}
        >
          Test Signal
        </Button>
      ),
    },
  ];

  // ---------------------------------------------------------------------------
  // Unified search handlers
  // ---------------------------------------------------------------------------
  const searchKeysMap = {
    measurement: ['name', 'appId'],
    domain: ['name'],
    impressions: ['name', 'dsp', 'tagId'],
  };

  const dataMap = {
    measurement: { data: measurementsTags, setFiltered: setFilteredMeasurementTags },
    domain: { data: publishersTags, setFiltered: setFilteredPublisherTags },
    impressions: { data: impressionsTags, setFiltered: setFilteredImpressionTags },
  };

  const handleSearch = (value) => {
    if (!value) {
      handleClear();
      return;
    }
    const { data, setFiltered } = dataMap[activeTab];
    if (data) {
      setFiltered(performFuzzySearch(data, searchKeysMap[activeTab], value));
    }
    setPubPage(1);
    setImpPage(1);
  };

  const handleClear = () => {
    setSearchValue('');
    const { data, setFiltered } = dataMap[activeTab];
    setFiltered(data);
    setPubVisible(10);
    setImpVisible(10);
    setPubPage(1);
    setImpPage(1);
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setSearchValue('');
    // Reset all filtered data to source
    setFilteredMeasurementTags(measurementsTags);
    setFilteredPublisherTags(publishersTags);
    setFilteredImpressionTags(impressionsTags);
    // Reset mobile load-more counts
    setPubVisible(10);
    setImpVisible(10);
    setPubPage(1);
    setImpPage(1);
    // Preserve status in URL, update tab
    params.set('tab', key);
    params.delete('tagId');
    params.delete('name');
    params.delete('search');
    history.push({ pathname: location.pathname, search: `?${params.toString()}` });
  };

  const handleTestSignal = (record) => {
    clearBasicTagActivity();
    testTag({
      appId: record.tagId || record.appId,
      orgId: record.orgId,
      tagId: record.id,
    });
    message.info(
      `Signal Tag Test for ${record.name} initiated. May take a couple of minutes to complete.`
    );
  };

  const renderCreateButton = () => {
    if (activeTab === 'measurement') {
      return (
        <Button
          type="primary"
          shape="round"
          onClick={() => history.push('/tags/manager?source=measurement')}
        >
          {isMobile ? <PlusOutlined /> : 'Create New Tag'}
        </Button>
      );
    }
    if (activeTab === 'domain') {
      return <CreatePublisherDomainTag />;
    }
    if (activeTab === 'impressions') {
      return (
        <CreateImpressionsTag
          onSubmit={onImressionsTagCreate}
          refetch={refetch}
          advertisers={advertisers}
        />
      );
    }
    return null;
  };

  const renderToolbar = () => (
    <Space size="small" wrap className="tags-main__toolbar">
      <Select
        defaultValue="live"
        value={status}
        className="tags-main__status-select"
        onChange={(val) => {
          if (status) params.delete('status');
          params.set('tab', activeTab);
          params.append('status', val);
          history.push({ pathname: location.pathname, search: `?${params.toString()}` });
        }}
      >
        <Select.Option value="live">
          <span className="tags-main__status-dot tags-main__status-dot--live" />
          LIVE
        </Select.Option>
        <Select.Option value="disabled">
          <span className="tags-main__status-dot tags-main__status-dot--archived" />
          ARCHIVED
        </Select.Option>
      </Select>
      <Search
        placeholder="Search tags..."
        prefix={<SearchOutlined />}
        value={searchValue}
        onChange={(e) => {
          const val = e.target.value;
          setSearchValue(val);
          if (!val) handleClear();
          else handleSearch(val);
        }}
        onSearch={handleSearch}
        allowClear
        className="tags-main__search-input"
      />
      {!isMobile && renderCreateButton()}
    </Space>
  );

  const renderFab = () => {
    if (!isMobile) return null;
    return (
      <div className="tags-fab">
        {renderCreateButton()}
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Tab content renderers
  // ---------------------------------------------------------------------------
  const renderMeasurementTagsContent = () => (
    <div>
      <div className="tags-main__alert-row">
        <DocumentationAlert />
      </div>
      <Card className="tags-card">
        <MainTable
          tags={filteredMeasurementTags}
          testTag={testTag}
          isAttentionSignals={isAttentionSignals}
        />
      </Card>
    </div>
  );

  return (
    <div className="tags-page">
      <div className="tags-page__header">
        <div className="tags-page__header-title">
          <TagOutlined className="tags-page__title-icon" />
          <span className="tags-page__title">Tags</span>
        </div>
        <div className="tags-page__header-actions">
          <Button
            type="default"
            shape="round"
            onClick={() => setHelpCenterVisible(true)}
            className="tags-main__help-center-btn"
          >
            <QuestionCircleOutlined />
            Tag Help Center
          </Button>
          <RequestAssistance onRequestAssistance={requestAssistance} />
        </div>
      </div>
      <PageContentLayout>
        {isAttentionSignals ? (
          <Tabs
            className="tags-main"
            activeKey={activeTab}
            onChange={handleTabChange}
            tabBarExtraContent={renderToolbar()}
          >
            <TabPane tab="Measurement Tags" key="measurement">
              {renderMeasurementTagsContent()}
            </TabPane>
            <TabPane tab="Publisher Domain Tags" key="domain">
              <Card className="tags-card">
                {isCardView ? (
                  <>
                    <div className="tags-mobile-card-list">
                      {filteredPublisherTags
                        ?.slice(0, pubVisible)
                        .map((record) => (
                          <TagMobileCard
                            key={record.id}
                            record={record}
                            type="publisher"
                            onClick={() => history.push(`/tags/details/${record.id}?source=publisher`)}
                            onTestSignal={handleTestSignal}
                          />
                        ))}
                    </div>
                    {pubVisible < (filteredPublisherTags?.length || 0) && (
                      <div className="tags-mobile-load-more">
                        <Button
                          onClick={() => setPubVisible((prev) => prev + LOAD_MORE_BATCH)}
                          block
                        >
                          Load More ({(filteredPublisherTags?.length || 0) - pubVisible} remaining)
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <Table
                      aria-label="Publisher domain tags"
                      columns={publisherColumn}
                      dataSource={filteredPublisherTags?.slice((pubPage - 1) * pubPageSize, pubPage * pubPageSize)}
                      rowKey={(record) => record.id}
                      onRow={(record) => ({
                        onClick: () => {
                          history.push(`/tags/details/${record.id}?source=publisher`);
                        },
                      })}
                      scroll={{ x: 1320 }}
                      size="middle"
                      pagination={false}
                    />
                    <TagsPagination
                      current={pubPage}
                      pageSize={pubPageSize}
                      total={filteredPublisherTags?.length || 0}
                      onChange={setPubPage}
                      onPageSizeChange={(size) => { setPubPageSize(size); setPubPage(1); }}
                    />
                  </>
                )}
              </Card>
            </TabPane>
            <TabPane tab="Impression Tags" key="impressions">
              <Card className="tags-card">
                {isCardView ? (
                  <>
                    <div className="tags-mobile-card-list">
                      {filteredImpressionTags
                        ?.slice(0, impVisible)
                        .map((record) => (
                          <TagMobileCard
                            key={record.id}
                            record={record}
                            type="impression"
                            onClick={() => history.push(`/tags/details/${record.id}?source=impression`)}
                            onTestSignal={handleTestSignal}
                          />
                        ))}
                    </div>
                    {impVisible < (filteredImpressionTags?.length || 0) && (
                      <div className="tags-mobile-load-more">
                        <Button
                          onClick={() => setImpVisible((prev) => prev + LOAD_MORE_BATCH)}
                          block
                        >
                          Load More ({(filteredImpressionTags?.length || 0) - impVisible} remaining)
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <Table
                      aria-label="Impression tags"
                      columns={impressionColumn}
                      dataSource={filteredImpressionTags?.slice((impPage - 1) * impPageSize, impPage * impPageSize)}
                      rowKey={(record) => record.id}
                      onRow={(record) => ({
                        onClick: () => {
                          history.push(`/tags/details/${record.id}?source=impression`);
                        },
                      })}
                      scroll={{ x: 1160 }}
                      size="middle"
                      pagination={false}
                    />
                    <TagsPagination
                      current={impPage}
                      pageSize={impPageSize}
                      total={filteredImpressionTags?.length || 0}
                      onChange={setImpPage}
                      onPageSizeChange={(size) => { setImpPageSize(size); setImpPage(1); }}
                    />
                  </>
                )}
              </Card>
            </TabPane>
          </Tabs>
        ) : (
          <div>
            <div className="tags-page__header">
              <span className="tags-page__title">Measurement Tags</span>
              <div className="tags-page__header-actions">
                {renderToolbar()}
              </div>
            </div>
            {renderMeasurementTagsContent()}
          </div>
        )}
      </PageContentLayout>
      {renderFab()}
      <BackToTop />
      <TagHelpCenterModal
        visible={helpCenterVisible}
        onClose={() => setHelpCenterVisible(false)}
      />
    </div>
  );
};

export default withRouter(TagsMainView);
